/* eslint-disable no-shadow */
import React, { useState, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { Modal } from 'react-bootstrap';
import i18n from 'i18next';
import { connect } from 'react-redux';
import { ToastComponent } from '@syncfusion/ej2-react-notifications';

import {
  addOrUpdateTemplate,
  getEmailTemplates,
  deleteTemplate
} from '../../../services/accounts';
import UserSign from '../menu-user-sign';
import { setEmailTemplates } from '../../../actions/lexon';
import { Confirmation } from '../../notification/confirmation';
import { getStringSizeInKB } from '../../../utils/utils';

const AccountTemplate = ({
  showUserTemplatePopover,
  setShowUserTemplatePopover,
  lexon,
  setEmailTemplates
}) => {
  const { userId, lexonToken } = lexon;
  const [templates, setTemplates] = useState([]);
  const [editing, setEditing] = useState(false);
  const [currentTemplate, setCurrentTemplate] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [, setIsLoading] = useState(false);
  const [currentTemplates, setCurrentTemplates] = useState([]);
  const [editTemplateName, setEditTemplateName] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [deleteSelectedTemplate, setDeleteSelectedTemplate] = useState(null);
  const [showSizeErrorModal, setShowSizeErrorModal] = useState(false);
  const [toastObj, setToastObj] = useState(null);

  useEffect(() => () => {
    setShowUserTemplatePopover(false);
    return () => {
      if (toastObj) {
        toastObj.hide('All');
      }
    };
  }, []);

  const getTemplates = async () => {
    setIsLoading(true);
    const response = await getEmailTemplates({ userId, lexonToken });
    setCurrentTemplates(response.data);
    setIsLoading(false);
  };

  useEffect(() => {
    (async () => {
      getTemplates();
    })();
  }, [showUserTemplatePopover, editing]);

  useEffect(() => {
    setTemplates(currentTemplates || []);
    setEmailTemplates(currentTemplates || []);
  }, [currentTemplates]);

  const addNewTemplate = () => {
    setEditing(true);
    setCurrentTemplate('');
    setEditTemplateName('');
    setIsDefault(false);
  };

  const saveTemplate = async () => {
    try {
      if (currentTemplate === '' || editTemplateName === '') {
        toastObj.show({
          content: i18n.t('toasts.mandatoryFieldsError'),
          cssClass: 'e-toast-danger',
          icon: 'lf-icon lf-icon-close-round'
        });
        return;
      }

      if (getStringSizeInKB(currentTemplate) > 512) {
        setShowSizeErrorModal(true);
        return;
      }

      setIsLoading(true);
      const action = editing && editingTemplate ? 'update' : 'create';
      const actionUrl = editing && editingTemplate ? 'update' : 'add';
      let newTemplateData = {
        userId,
        name: editTemplateName,
        template: currentTemplate,
        isDefault,
        action,
        actionUrl
      };
      if (editingTemplate) {
        newTemplateData = {
          ...editingTemplate,
          ...newTemplateData
        };
      }
      await addOrUpdateTemplate(newTemplateData, lexonToken);
      getTemplates();
      setIsLoading(false);
      setEditing(false);
      setCurrentTemplate('');
      setEditTemplateName('');
      setIsDefault(false);
      setEditingTemplate(null);
      toastObj.show({
        content:
          action === 'create'
            ? i18n.t('toasts.templateCreatedSuccessfully')
            : i18n.t('toasts.templateUpdatedSuccessfully'),
        cssClass: 'e-toast-success',
        icon: 'lf-icon lf-icon-check-round'
      });
    } catch (error) {
      console.error(error);
      toastObj.show({
        content: i18n.t('toasts.templateCreateError'),
        cssClass: 'e-toast-danger',
        icon: 'lf-icon lf-icon-close-round'
      });
    }
  };

  const handleRemoveTemplate = async (template, editing = false) => {
    try {
      setIsLoading(true);
      await deleteTemplate({
        templateId: template._id,
        lexonToken
      });
      await getTemplates();
      setIsLoading(false);
      if (!editing) {
        toastObj.show({
          content: i18n
            .t('toasts.templateDeletedSuccessfully')
            .replace('{}', deleteSelectedTemplate?.name),
          cssClass: 'e-toast-success',
          icon: 'lf-icon lf-icon-check-round'
        });
      }
      setDeleteSelectedTemplate(null);
    } catch (error) {
      console.error(error);
      toastObj.show({
        content: i18n
          .t('toasts.templateDeleteError')
          .replace('{}', deleteSelectedTemplate?.name),
        cssClass: 'e-toast-danger',
        icon: 'lf-icon lf-icon-close-round'
      });
      setDeleteSelectedTemplate(null);
    }
  };

  const handleCancel = () => {
    if (editing) {
      setEditing(false);
      setCurrentTemplate('');
      setEditTemplateName('');
      setEditingTemplate(null);
      setIsDefault(false);
    } else {
      setShowUserTemplatePopover(false);
    }
  };

  return (
    <>
      <ToastComponent
        ref={toast => setToastObj(toast)}
        id="toast_pos"
        position={{ X: 'Right', Y: 'Top' }}
        animation={{ hide: { duration: '1' }, show: { duration: '200' } }}
        timeOut={7000}
        showCloseButton
      />
      <Confirmation
        initialModalState={deleteConfirmationOpen}
        onAccept={() => {
          handleRemoveTemplate(deleteSelectedTemplate);
          setDeleteConfirmationOpen(false);
        }}
        onCancel={() => {
          setDeleteSelectedTemplate(null);
          setDeleteConfirmationOpen(false);
        }}
        message={i18n
          .t('alerts.templateDeleteConfirmation')
          .replace('{}', deleteSelectedTemplate?.name)}
        titleAccept={i18n.t('general.remove')}
        titleCancel={i18n.t('general.cancel')}
        confirmType="confirm-info"
        iconType="lf-icon lf-icon-information"
      />
      <Confirmation
        initialModalState={showSizeErrorModal}
        onAccept={() => setShowSizeErrorModal(false)}
        message={i18n.t('toasts.templateSizeError')}
        titleAccept={i18n.t('general.accept')}
        confirmType="confirm-error"
        iconType="lf-icon lf-icon-close-round"
      />
      <Modal
        show={showUserTemplatePopover}
        onHide={() => {
          setEditing(false);
          setShowUserTemplatePopover(false);
        }}
        size="xl"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        className="user-template-modal"
      >
        <Modal.Header>
          <div className="user-template-modal-header">
            <Modal.Title>{i18n.t('email-template.title')}</Modal.Title>
          </div>
        </Modal.Header>
        <Modal.Body>
          <div className="account-template-body-container">
            {templates.length === 0 && !editing && (
              <div className="account-template-empty-body">
                <div className="account-template-empty-body-icon">
                  <img
                    src="/assets/icons/firma/plantillas-logo.svg"
                    alt="empty"
                  />
                </div>
                <div>
                  <div className="body-text1">
                    {i18n.t('email-template.professional_template_text')}
                  </div>
                  <div className="body-text2">
                    <span onClick={() => addNewTemplate()}>
                      {i18n.t('email-template.new_template_text')}
                    </span>{' '}
                    {i18n.t('email-template.use_when_you_need_text')}
                  </div>
                </div>
              </div>
            )}

            {editing && (
              <div className="mt-4">
                <div className="d-flex justify-content-end account-template-editing-mandatory">
                  <span className="text-danger">*</span>
                  {i18n.t('general.required_field')}
                </div>
                <div className="account-template-editing-title">
                  {i18n.t('email-template.template_name_text')}
                  <span className="text-danger">*</span>
                </div>
                <input
                  placeholder={i18n.t('email-template.template_name_text')}
                  type="text"
                  className="form-control account-template-editing-title-input"
                  value={editTemplateName}
                  onChange={e => setEditTemplateName(e.target.value)}
                />

                <div className="d-flex my-3">
                  <div className="is-default-wrapper">
                    <i
                      className={`lf-icon icon-default ${
                        isDefault
                          ? 'lf-icon-switch-right-full'
                          : 'lf-icon-switch-left'
                      }`}
                      onClick={() => setIsDefault(!isDefault)}
                    />
                    <span className="default-text">
                      {i18n.t('email-template.select_default_template_text')}
                    </span>
                  </div>
                </div>
                <div className="account-template-editing-title">
                  {i18n.t('email-template.template')}
                  <span className="text-danger">*</span>
                </div>
                <UserSign
                  onChange={setCurrentTemplate}
                  defaultValue={currentTemplate}
                />
              </div>
            )}

            {templates.length > 0 && !editing && (
              <>
                {templates.map(template => (
                  <div
                    className="d-flex justify-content-between align-items-center mt-4 account-template-list-item"
                    key={template.name}
                  >
                    <div className="inter-medium-16 account-template-list-template">
                      <span>{template.name}</span>
                      {template.isDefault && (
                        <span className="account-template-deafult-tag ml-2">
                          {i18n.t('email-template.by_default')}
                        </span>
                      )}
                    </div>
                    <div className="d-flex">
                      <div className="mr-4">
                        <img
                          src="/assets/icons/firma/edit.svg"
                          className="lf-icon lf-icon-edit"
                          alt="edit"
                          onClick={() => {
                            setEditTemplateName(template.name);
                            setCurrentTemplate(template.template);
                            setEditingTemplate(template);
                            setIsDefault(template.isDefault);
                            setEditing(true);
                          }}
                        />
                      </div>
                      <div>
                        <img
                          src="/assets/icons/firma/delete.svg"
                          className="lf-icon lf-icon-trash"
                          alt="delete"
                          style={{ marginRight: '10px' }}
                          onClick={() => {
                            setDeleteSelectedTemplate(template);
                            setDeleteConfirmationOpen(true);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <div
                  className="account-add-template mt-4"
                  onClick={() => addNewTemplate()}
                >
                  <img
                    src="/assets/icons/create.svg"
                    className="lf-icon lf-icon-add-round"
                  />
                  <span className="inter-medium-16 ml-2">
                    {i18n.t('email-template.new_template')}
                  </span>
                </div>
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="d-flex justify-content-end modal-btn-container pt-4">
            <button
              type="button"
              className="mr-left font-weight-bold btn-outline-primary btn btn-secondary"
              onClick={handleCancel}
            >
              {editing ? i18n.t('general.cancel') : i18n.t('general.close')}
            </button>

            {!editing ? (
              <button
                type="button"
                className="mr-left font-weight-bold btn-primary btn"
                onClick={() => setEditing(true)}
              >
                {i18n.t('general.create')}
              </button>
            ) : (
              <button
                type="button"
                className="mr-left font-weight-bold btn-primary btn"
                onClick={saveTemplate}
              >
                {i18n.t('general.save')}
              </button>
            )}
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
};

const mapStateToProps = state => ({
  lexon: state.lexon
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      setEmailTemplates
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(AccountTemplate);
